import { ContextConsumer } from '@lit/context'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'
import { isServer, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import Icon from '../icon/Icon.js'
import {
  fallback,
  resolveTranslation,
  subscribeToDocumentLang,
  subscribeToTranslationRegistration,
} from '../localization/translation.js'
import { modalContext } from './modal-context.js'
import style from './parts.css'

Icon.registerIcon(closeIcon)

/**
 * A standalone close control for a composed [Modal](/components/modal/).
 *
 * Unlike the modal's built-in close button (which is locked into the header
 * zone), this part can be placed **anywhere** inside `nord-modal` — floated over
 * completely custom content, dropped into a footer as a "Cancel" button, or
 * nested inside your own layout. Clicking it dismisses the nearest
 * `nord-modal` through the same cancelable flow as the built-in button (the
 * modal's `cancel` event still fires and can be prevented).
 *
 * When left empty it renders the default close "✕" icon button. Give it your own
 * control (a `nord-button`, `button`, or link) and it becomes a transparent
 * wrapper that turns that control into a close trigger:
 *
 * The part is unpositioned by default — place it with your own styles (the
 * modal frame is `position: relative`, so it is the positioning context):
 *
 * ```html
 * <!-- floating icon button over custom content -->
 * <nord-modal-close
 *   style="position: absolute; inset-block-start: var(--n-space-s); inset-inline-end: var(--n-space-s)"
 * ></nord-modal-close>
 *
 * <!-- any control becomes a close trigger -->
 * <nord-modal-close><nord-button>Cancel</nord-button></nord-modal-close>
 * ```
 *
 * @status draft
 * @category overlay
 * @lightDom true
 *
 * @slot - Optional custom close control. When empty, a default icon button is rendered.
 */
@customElement('nord-modal-close')
export default class ModalClose extends DraftComponentMixin(LitElement) {
  /** Overrides the accessible label of the default close button. */
  @property() label?: string

  /** The default icon button, when this part renders its own control. */
  private defaultButton?: HTMLButtonElement

  /** Unsubscribe handle for document-language changes. */
  private unsubscribeLang?: () => void
  private unsubscribeTranslationRegistration?: () => void
  private langObserver?: MutationObserver
  private modal = new ContextConsumer(this, {
    context: modalContext,
    subscribe: true,
  })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style, 'nord-modal-parts')
    this.addEventListener('click', this.handleClick)
    this.unsubscribeLang = subscribeToDocumentLang(this.handleLanguageChange)
    this.unsubscribeTranslationRegistration = subscribeToTranslationRegistration(this.handleLanguageChange)
    if (!isServer) {
      this.langObserver = new MutationObserver(this.handleLanguageChange)
      this.langObserver.observe(this, { attributes: true, attributeFilter: ['lang'] })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
    this.unsubscribeLang?.()
    this.unsubscribeTranslationRegistration?.()
    this.langObserver?.disconnect()
    this.unsubscribeLang = undefined
    this.unsubscribeTranslationRegistration = undefined
    this.langObserver = undefined
  }

  protected createRenderRoot() {
    return this
  }

  protected firstUpdated() {
    // When the consumer supplies no control of their own, render the default
    // icon button. LitElement's default render() returns `noChange`, so an
    // element appended here is left untouched by subsequent updates.
    if (this.childElementCount === 0) {
      this.defaultButton = this.createDefaultButton()
      this.appendChild(this.defaultButton)
    }
  }

  protected updated() {
    this.defaultButton?.querySelector('nord-icon')?.setAttribute('label', this.resolvedLabel)
  }

  /**
   * Resolves the accessible label: an explicit `label` wins, otherwise the
   * shared `nord-modal` `closeLabel` translation for the current document
   * language (falling back to English). We resolve the `nord-modal` term
   * directly rather than via `LocalizeController`, which keys translations by
   * the host tag name (`nord-modal-close` has no entry of its own).
   */
  private get resolvedLabel(): string {
    if (this.label) {
      return this.label
    }
    const lang = this.lang || (isServer ? 'en-US' : document.documentElement.lang)
    const translation = resolveTranslation(lang)
    return translation['nord-modal']?.closeLabel ?? fallback['nord-modal'].closeLabel
  }

  private createDefaultButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'n-modal-close'

    const icon = document.createElement('nord-icon')
    icon.setAttribute('name', 'interface-close-small')
    icon.setAttribute('size', 's')
    icon.setAttribute('label', this.resolvedLabel)
    button.appendChild(icon)

    return button
  }

  private handleLanguageChange = () => {
    this.requestUpdate()
  }

  private handleClick = (event: Event) => {
    this.modal.value?.requestClose(event)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-modal-close': ModalClose
  }
}
